/**
 * run_flow tool - Trigger a manual Power Automate flow
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const runFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID) to run"),
  triggerBody: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("Input data to pass to the flow trigger. Must match the trigger's input schema."),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  waitForCompletion: z
    .boolean()
    .optional()
    .default(false)
    .describe("If true, poll until the run completes and return the final status."),
  pollIntervalMs: z
    .number()
    .optional()
    .default(2000)
    .describe("Polling interval in milliseconds when waiting for completion (default: 2000)."),
  timeoutMs: z
    .number()
    .optional()
    .default(60000)
    .describe("Maximum time to wait for completion in milliseconds (default: 60000)."),
});

export type RunFlowInput = z.infer<typeof runFlowInputSchema>;

export const runFlowTool = {
  name: "run_flow",
  description:
    "Trigger a Power Automate flow to run immediately. Works with any trigger type (manual, scheduled, event-based). Optionally pass input data and wait for the run to complete.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID) to run",
      },
      triggerBody: {
        type: "object",
        description: "Input data to pass to the flow trigger",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      waitForCompletion: {
        type: "boolean",
        description: "If true, wait for the run to complete",
      },
      pollIntervalMs: {
        type: "number",
        description: "Polling interval in ms (default: 2000)",
      },
      timeoutMs: {
        type: "number",
        description: "Timeout in ms (default: 60000)",
      },
    },
    required: ["flowId"],
  },
};

export async function handleRunFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = runFlowInputSchema.parse(input);

  logger.info(
    { flowId: parsed.flowId, hasBody: !!parsed.triggerBody },
    "Executing run_flow"
  );

  // Verify the flow exists and check its state
  const flow = await api.getFlow(parsed.flowId, parsed.environment);

  if (flow.properties.state !== "Started") {
    return `Cannot run "${flow.properties.displayName}" - flow is ${flow.properties.state}.\n\nUse toggle_flow to start it first.`;
  }

  // Note: All flows can be triggered via API regardless of trigger type
  // The API will execute the flow's actions directly

  // Trigger the flow
  let triggerResponse;
  try {
    triggerResponse = await api.triggerFlow(
      parsed.flowId,
      parsed.triggerBody ? { body: parsed.triggerBody } : undefined,
      parsed.environment
    );
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    const details = error && typeof error === "object" && "details" in error
      ? JSON.stringify((error).details).slice(0, 300)
      : "";
    return `Failed to trigger flow: ${msg} ${details}`;
  }

  // The trigger call does NOT tell you which run it started: a Request
  // trigger's /run returns 202 with an empty body, and its response headers
  // carry no run id either (checked against a live flow — there is no
  // x-ms-workflow-run-id and no location header). So the run has to be
  // identified by looking at the flow's recent runs.
  const runId = triggerResponse.name;

  const runs = await api.getFlowRuns(parsed.flowId, parsed.environment, 5);
  let currentRun = runs.find((r) => r.name === runId) ?? runs[0];

  // If not waiting, return immediately
  if (!parsed.waitForCompletion) {
    const identified = runId ?? currentRun?.name;
    return identified
      ? `Triggered "${flow.properties.displayName}" - Run: ${identified}`
      : // Saying "Run: undefined" implied something had gone wrong when the
        // run had in fact started. Name the limitation instead.
        `Triggered "${flow.properties.displayName}". The trigger call does not return a run id, and no run has appeared yet — call get_runs on this flow to see it.`;
  }

  // Poll for completion
  const startTime = Date.now();
  const timeout = parsed.timeoutMs ?? 60000;
  const pollInterval = parsed.pollIntervalMs ?? 2000;

  while (
    currentRun &&
    Date.now() - startTime < timeout &&
    ["Running", "Waiting"].includes(currentRun.properties?.status ?? "Running")
  ) {
    await new Promise((resolve) => setTimeout(resolve, pollInterval));

    // Get updated run status
    const updatedRuns = await api.getFlowRuns(parsed.flowId, parsed.environment, 5);
    const matchingRun = updatedRuns.find((r) => r.name === runId);

    if (matchingRun) {
      currentRun = matchingRun;
    } else {
      break;
    }
  }

  const finalStatus = currentRun?.properties?.status ?? "Unknown";
  const isCompleted = !["Running", "Waiting"].includes(finalStatus);
  const timedOut = !isCompleted && Date.now() - startTime >= timeout;
  const duration = currentRun?.properties?.endTime && currentRun?.properties?.startTime
    ? `${((new Date(currentRun.properties.endTime).getTime() - new Date(currentRun.properties.startTime).getTime()) / 1000).toFixed(1)}s`
    : "";

  if (timedOut) {
    return `Timeout waiting for "${flow.properties.displayName}" - Status: ${finalStatus}`;
  }

  const errorMsg = currentRun?.properties?.error?.message;
  if (errorMsg) {
    return `${finalStatus}: "${flow.properties.displayName}" ${duration} - ${errorMsg}`;
  }

  return `${finalStatus}: "${flow.properties.displayName}" ${duration}`.trim();
}
